import * as React from "react";
import { DangerAlert } from "components/common/Alerts";
import { Breadcrumb, BreadcrumbLink } from "components/common/Breadcrumb";
import ContentLoading from "components/common/ContentLoading";
import apifetch from "utils/apifetch";
import extractErrorMessage from "utils/extractErrorMessage";
import { RouteComponentProps, Link } from "react-router-dom";
import isInRole from "utils/isInRole";
import RoleName from "enums/RoleName";

interface State {
    isLoading: boolean;
    files: string[];
    selectedFile?: string;
    logLines: string[];
    loadingContent: boolean;
    visibleLineCount: number;
    errorMessage?: string;
}

export default class LogsPage extends React.Component<RouteComponentProps<{}>, State> {
    constructor(props: RouteComponentProps<{}>) {
        super(props);
        this.state = {
            isLoading: true,
            files: [],
            logLines: [],
            loadingContent: false,
            visibleLineCount: 500,
            errorMessage: undefined,
        };
    }

    public componentWillMount() {
        if (!isInRole(RoleName.Admin)) {
            this.props.history.push("/account/unauthorized");
            return false;
        }
        return true;
    }

    public async componentDidMount() {
        await this.refreshLogs();
    }

    private async loadLogContent(fileName: string) {
        this.setState({ loadingContent: true, errorMessage: undefined });
        try {
            const response = await apifetch(`logs/${fileName}`, undefined, true);
            if (response.ok) {
                const data = await response.json();
                const content = (data.content as string) || "";
                const lines = content ? content.split(/\r?\n/) : [];
                this.setState({ selectedFile: fileName, logLines: lines, visibleLineCount: 500, loadingContent: false });
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({ selectedFile: fileName, logLines: ["Failed to load content."], visibleLineCount: 500, loadingContent: false, errorMessage: errorText });
            }
        } catch (err) {
            console.error(err);
            this.setState({ selectedFile: fileName, logLines: ["Error loading content."], visibleLineCount: 500, loadingContent: false, errorMessage: "An unexpected error occurred." });
        }
    }

    private async handleDeleteClick(e: React.MouseEvent, fileName: string) {
        e.stopPropagation();

        const confirmed = window.confirm(`Are you sure you want to delete "${fileName}"?`);
        if (!confirmed) return;

        try {
            const response = await apifetch(`logs/${fileName}`, { method: "DELETE" }, true);
            if (response.ok) {
                this.setState((prevState) => ({
                    files: prevState.files.filter((f) => f !== fileName),
                    selectedFile:
                        prevState.selectedFile === fileName ? undefined : prevState.selectedFile,
                    logLines:
                        prevState.selectedFile === fileName ? [] : prevState.logLines,
                    visibleLineCount: 500,
                    errorMessage: undefined,
                }));
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({ errorMessage: errorText });
            }
        } catch (err) {
            console.error(err);
            this.setState({ errorMessage: "An unexpected error occurred while deleting the log." });
        }
    }

    private async refreshLogs() {
        this.setState({ isLoading: true, errorMessage: undefined });
        try {
            const response = await apifetch("logs", undefined, true);
            if (response.ok) {
                const files = await response.json();
                this.setState({ files, isLoading: false });
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({ isLoading: false, errorMessage: errorText });
            }
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false, errorMessage: "An unexpected error occurred while refreshing logs." });
        }
    }


    public render() {
        const { isLoading, files, selectedFile, loadingContent } = this.state;

        if (isLoading) return (
            <div className="container">
                <ContentLoading />
            </div>
        );

        return (
            <div className="container settings-logs">
                <div className="animated fadeIn">
                    <Breadcrumb>
                        <BreadcrumbLink to="/">Home</BreadcrumbLink>
                        <BreadcrumbLink to="/settings">Settings</BreadcrumbLink>
                        <BreadcrumbLink isActive={true}>Logs</BreadcrumbLink>
                    </Breadcrumb>
                    {this.state.errorMessage && (
                        <DangerAlert name="dangerAlert">{this.state.errorMessage}</DangerAlert>
                    )}

                    <div className="card card--datatables">
                        <div className="card-block">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="card__title mb-0">Application Logs</h4>
                                <div>
                                    <button className="btn btn-sm btn-primary mr-2" onClick={() => this.refreshLogs()}>
                                        Refresh
                                    </button>
                                    <Link to="/settings" className="btn btn-secondary">
                                        ← Back
                                    </Link>
                                </div>
                            </div>


                            <div className="row">
                                <div className="col-md-5">
                                    <div
                                        style={{
                                            maxHeight: "500px",
                                            overflowY: "auto",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "0.25rem",
                                        }}
                                    >
                                        <table className="table table-hover mb-0">
                                            <thead className="thead-light" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                                                <tr>
                                                    <th>Log Files</th>
                                                    <th className="text-right">Actions</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {files.length > 0 ? (
                                                    files.map((file) => (
                                                        <tr
                                                            key={file}
                                                            style={{
                                                                backgroundColor: selectedFile === file ? "#f0f8ff" : "transparent",
                                                            }}
                                                        >
                                                            <td
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => this.loadLogContent(file)}
                                                            >
                                                                {file}
                                                            </td>
                                                            <td className="text-right">
                                                                <button
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={(e) => this.handleDeleteClick(e, file)}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={2}>No log files found</td>
                                                    </tr>
                                                )}
                                            </tbody>

                                        </table>
                                    </div>
                                </div>


                                <div className="col-md-7">
                                    {loadingContent ? (
                                        <ContentLoading />
                                    ) : this.state.logLines.length > 0 ? (
                                        <div
                                            className="log-content border rounded p-3"
                                            style={{
                                                backgroundColor: "#f9f9f9",
                                                whiteSpace: "pre-wrap",
                                                height: "500px",
                                                overflowY: "auto",
                                            }}
                                        >
                                            <h5>{selectedFile}</h5>
                                            <hr />
                                            <code>
                                                {this.state.logLines
                                                    .slice(0, this.state.visibleLineCount)
                                                    .join("\n")}
                                            </code>
                                            {this.state.logLines.length > this.state.visibleLineCount && (
                                                <div className="text-center mt-2">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() =>
                                                            this.setState((prev) => ({
                                                                visibleLineCount: prev.visibleLineCount + 500,
                                                            }))
                                                        }
                                                    >
                                                        Load More
                                                    </button>
                                                    <span className="ml-2 text-muted">
                                                        Showing {Math.min(this.state.visibleLineCount, this.state.logLines.length)} of {this.state.logLines.length} lines
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ) : selectedFile ? (
                                        <p>This log file is empty.</p>
                                    ) : (
                                        <p>Select a log file to view its contents.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./ClientApp/components/settings/LogsPage.tsx